import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
// import NProgress from 'nprogress';
import EventBus from '@/utilities/event-bus';
import { authApi } from '@/utilities/axios';
import { showToast } from '@/utilities/toast-handler';
import { useAuthStore } from '@/modules/auth/store';
import {  getPage, getPerPage } from '@/composables/PaginationComposable';
import { formatCommentDate,  formatDisplayDate } from '@/utilities/date-formatter';

const CHECKLIST_URL = process.env.VUE_APP_API_VERSION + '/checklists';
const CHECKLIST_DETAILS_URL = process.env.VUE_APP_API_VERSION + '/checklists_details';
export const useChecklistStore = defineStore('checklist', () => {
    const authStore = useAuthStore();
    const company_id = computed(() => {
        return authStore.getLoggedUserCompanyId;
    });

    const sortParams = ref('created_at desc');
    //state
    const editId = ref(null);
    const loading = ref(false);
    const checklists = ref([]);
    const totalRecords = ref(null);
    const checklistTypes = ref([]);

    const checklist = ref({
        checklist_type_id: null,
        station_id: null,
        date_from: null,
        date_to: null,
        items: [{ question: '', response: null, comment: ""  }]
    });

    const checklistType = ref({
        name:null,
        company_id: null,
        interval_days:null
    })

    const search = ref({
        checklist_type_id: null,
        station_id: null,
        stage: null,
        created_at_from: null,
        created_at_to: null,
        pagination:true
    });

    const params = ref({
        rows: 10,
        sortField: null,
        sortOrder: null,
        page: null,
        sortingInfo: 'created_at desc',
    });


    const fetchCheckListType = async () => {
        try {
            // NProgress.start();
            loading.value = true;
            const response = await authApi.get(`/v2/checklists/types?paginate=false`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                checklistTypes.value = response.data;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch checklist type');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };


    const fetchAllCheckList = async (list) => {
        try {

            // NProgress.start();
            loading.value = true;
            let page_size = getPerPage(list);
            let page = getPage(list);
            // const sortOrder = getOrderForHttp(list)

            // const LINK = CHECKLIST_URL + '?company_id=' + company_id.value + '&page_size=' + page_size + '&page=' + (page + 1);

            const response = await authApi.get(CHECKLIST_URL, {
                params: {
                    company_id: company_id.value,
                    ...search.value,
                    page_size: page_size,
                    page: page + 1,
                    sort: sortParams.value,
                },
            });
            if (response.statusCode === 200) {
                checklists.value = response.data.data;
                totalRecords.value = response.data.total;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to fetch checklist');
        } finally {
            // NProgress.done();
            loading.value = false;
        }
    };

    const createChecklist = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...checklist.value, company_id: company_id.value };
            const response = await authApi.post(CHECKLIST_URL, bodyParams);
            if (response.statusCode === 201) {
                // push the data into the appliances.value
                checklists.value?.push(response.data);
                showToast(response.toast, 'New Checklist created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const createChecklistItems = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...checklist.value, company_id: company_id.value };
            const response = await authApi.post(CHECKLIST_URL+'/items', bodyParams);
            if (response.statusCode === 201) {
                // push the data into the appliances.value
                    // checklists.value?.push(response.data);
                showToast(response.toast, 'New Checklist items created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const createChecklistType = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...checklistType.value, company_id: company_id.value };
            const response = await authApi.post(CHECKLIST_URL+'/types', bodyParams);
            if (response.statusCode === 201) {
                // push the data into the appliances.value
                checklistTypes.value?.push(response.data);
                showToast(response.toast, 'New Type created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create Type');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const updateChecklist = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...checklist.value, company_id: company_id.value };
            const response = await authApi.patch(`${CHECKLIST_URL}/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                const existingChecklist = checklists.value.find((checklist) => checklist.id === editId.value);
                if (existingChecklist) {
                    const existingChecklistIndex = checklists.value.indexOf(existingChecklist);
                    if (existingChecklistIndex !== -1) {
                        // replace the updated data into the checklist.value index
                        checklists.value[existingChecklistIndex] = response.data;
                        showToast(response.toast, 'Checklist updated successfully');
                        return true;
                    }
                }
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const processChecklist = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...checklist.value, company_id: company_id.value };
            const response = await authApi.patch(`${CHECKLIST_URL}/process/${editId.value}`, bodyParams);
            if (response.statusCode === 200) {
                const existingChecklist = checklists.value.find((checklist) => checklist.id === editId.value);
                if (existingChecklist) {
                    const existingChecklistIndex = checklists.value.indexOf(existingChecklist);
                    if (existingChecklistIndex !== -1) {
                        // replace the updated data into the checklist.value index
                        checklists.value[existingChecklistIndex] = response.data;
                        showToast(response.toast, 'Checklist updated successfully');
                        return true;
                    }
                }
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const deleteChecklist = async (checklistId) => {
        try {
            EventBus.emit('show-operation-loader');
            const response = await authApi.delete(`${CHECKLIST_URL}/${checklistId}`, {
                params: {
                    company_id: company_id.value,
                },
            });
            if (response.statusCode === 200) {
                // remove it from the array after deletion
                checklists.value = checklists.value?.filter((checklist) => checklist.id !== checklistId);
                showToast(response.toast, 'Checklist deleted successfully');
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to delete checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    const initiateChecklist = async () => {
        try {
            EventBus.emit('show-operation-loader');
            const bodyParams = { ...search.value, company_id: company_id.value };
            const response = await authApi.post(CHECKLIST_URL, bodyParams);
            if (response.statusCode === 201) {
                // push the data into the appliances.value
                checklists.value?.push(response.data);
                showToast(response.toast, 'New Checklist created successfully');
                return true;
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to create checklist');
        } finally {
            EventBus.emit('hide-operation-loader');
        }
    };

    // helpers
    const initChecklist = () => {
        editId.value = null,
        checklist.value.checklist_type_id = null,
        checklist.value.station_id = null,
        checklist.value.date_from = null,
        checklist.value.date_to = null,
        checklist.value.items = [{ question: '' }]
    };

    const loadChecklistBody = async (checklistData) => {
        if (!checklistData || typeof checklistData !== "object") {
            return;
        }

        loading.value = true

        try {
            // 1. Call backend to get items and responses for this checklist
            const response = await authApi.get(CHECKLIST_DETAILS_URL, {
                params: {
                    company_id: company_id.value,
                    checklist_id: checklistData.id,
                },
            });
            if (response.statusCode === 200) {

                // 2. Prepare the full checklist object
                checklist.value = {
                    checklist_type_id: checklistData?.checklist_type_id ?? null,
                    station_id: checklistData?.station_id ?? null,
                    date_from: checklistData?.date_from ?? null,
                    date_to: checklistData?.date_to ?? null,
                    items: Array.isArray(response.data) ? response.data : []
                };
            }
            // 3. Store edit ID
            editId.value = checklistData?.id ?? null;
        } catch (error) {
            console.error("Failed to load checklist items:", error);
        }finally {
            loading.value = false;
        }

    };


    // getters
    const allChecklist = computed(() => {
        return checklists.value?.map((checklist) => {
            return {
                ...checklist,
                type:checklist?.type?.name  ?? '',
                station:checklist?.station?.name ?? '',
                created_at: formatCommentDate(checklist.created_at),
            };
        });
    });


    const selective = computed(() => {
        if (!checklists.value || !editId.value) {
            return { station_name: "Unknown name", checklistTypeName: "Unknown type", date_from: "", date_to : "" };
        }
        const checklist = checklists.value.find(checklist => checklist.id === editId.value);
        return {
            station_name: checklist?.station?.name || "",
            checklistTypeName: checklist?.type?.name || "",
            date_from: formatDisplayDate(checklist?.date_from) || "",
            date_to:  formatDisplayDate(checklist?.date_to) || "",
        };
    });


    const initSearch = () => {
        search.value.checklist_type_id = null;
        search.value.station_id = null;
        search.value.stage = null;
        search.value.created_at_from = null;
        search.value.created_at_to = null;
    };

    const initChecklistType = () => {
            checklistType.value.name = null,
            checklistType.value.company_id = null,
            checklistType.value.interval_days = null
    };

    return {
        company_id,
        sortParams,
        //states
        checklist,
        checklists,
        editId,
        loading,
        totalRecords,

        search,
        checklistType,
        checklistTypes,
        params,

        //actions
        fetchCheckListType,
        fetchAllCheckList,
        createChecklist,
        createChecklistItems,
        createChecklistType,
        updateChecklist,
        deleteChecklist,
        processChecklist,
        initiateChecklist,

        //helpers
        initChecklist,
        loadChecklistBody,
        initSearch,
        initChecklistType,

    //     getter
        allChecklist,
        selective
    };
});
